import { format, parseISO } from 'date-fns';
import {
  CheckCircle2,
  MapPin,
  Pencil,
  Plus,
  RotateCcw,
  Trash2,
  UserRound,
  Wrench,
} from 'lucide-react';
import { useState } from 'react';

import { getErrorMessage } from '@/shared/errors/app-error';
import { useUser } from '@/shared/hooks/useUser';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';

import { useDeleteSafetyIssue, useUpdateSafetyIssueStatus } from './hooks';
import { SafetyIssueForm } from './SafetyIssueForm';
import type { SafetyIssue, SafetyPinDraft } from './types';

type DialogMode = 'details' | 'create' | 'edit' | 'delete';

interface SafetyPinDetailsDialogProps {
  projectId: number;
  layoutName: string;
  pin: SafetyPinDraft;
  issues: SafetyIssue[];
  onClose: () => void;
}

const creatorName = (issue: SafetyIssue) => {
  if (!issue.created_by) return 'Unknown user';
  const fullName = `${issue.created_by.first_name} ${issue.created_by.last_name}`.trim();
  return fullName || issue.created_by.email;
};

export const SafetyPinDetailsDialog = ({
  projectId,
  layoutName,
  pin,
  issues,
  onClose,
}: SafetyPinDetailsDialogProps) => {
  const { user } = useUser();
  const updateStatus = useUpdateSafetyIssueStatus(projectId);
  const deleteIssue = useDeleteSafetyIssue(projectId);
  const [mode, setMode] = useState<DialogMode>('details');
  const [activeIssue, setActiveIssue] = useState<SafetyIssue | null>(null);
  const currentUserId = user ? Number(user.id) : null;
  const error = updateStatus.error ?? deleteIssue.error;

  const showDetails = () => {
    setActiveIssue(null);
    setMode('details');
  };

  const dialogTitle =
    mode === 'create'
      ? 'Add safety issue'
      : mode === 'edit'
        ? 'Edit safety issue'
        : mode === 'delete'
          ? 'Delete safety issue?'
          : 'Safety issues at this pin';

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
          <DialogDescription>
            {mode === 'details'
              ? `${issues.length} ${issues.length === 1 ? 'issue' : 'issues'} on ${layoutName}`
              : mode === 'delete'
                ? 'This action cannot be undone.'
                : `This issue is attached to the selected pin on ${layoutName}.`}
          </DialogDescription>
        </DialogHeader>

        {mode === 'create' ? (
          <SafetyIssueForm
            projectId={projectId}
            pin={pin}
            onSuccess={showDetails}
            onCancel={showDetails}
          />
        ) : mode === 'edit' && activeIssue ? (
          <SafetyIssueForm
            projectId={projectId}
            pin={{
              ...pin,
              locationId: activeIssue.location ?? pin.locationId,
              locationName: activeIssue.location_detail?.name ?? pin.locationName,
            }}
            issue={activeIssue}
            onSuccess={showDetails}
            onCancel={showDetails}
          />
        ) : mode === 'delete' && activeIssue ? (
          <div className="space-y-5">
            <div className="rounded-[13px] border border-destructive/30 bg-destructive/10 p-4">
              <p className="text-sm font-semibold text-foreground">{activeIssue.description}</p>
              <p className="mt-2 text-sm text-muted-foreground">
                Deleting this record removes it from the layout for everyone.
              </p>
            </div>
            {error && (
              <p role="alert" className="text-sm text-destructive">
                {getErrorMessage(error)}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={showDetails} disabled={deleteIssue.isPending}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                disabled={deleteIssue.isPending}
                onClick={() => {
                  deleteIssue.mutate(activeIssue.id, {
                    onSuccess: () => {
                      if (issues.length === 1) onClose();
                      else showDetails();
                    },
                  });
                }}
              >
                <Trash2 />
                Delete issue
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex justify-end">
              <Button size="sm" className="gap-2" onClick={() => setMode('create')}>
                <Plus />
                Add new
              </Button>
            </div>

            <div className="max-h-[58vh] space-y-3 overflow-y-auto pr-1">
              {issues.map((issue) => {
                const canModify = currentUserId != null && issue.created_by?.id === currentUserId;
                const isOpen = issue.status === 'OPEN';
                return (
                  <article
                    key={issue.id}
                    className="rounded-[13px] border border-border bg-muted/40 p-4"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <p className="whitespace-pre-wrap text-sm font-semibold leading-6">
                        {issue.description}
                      </p>
                      <Badge variant={isOpen ? 'destructive' : 'secondary'}>
                        {isOpen ? 'Open' : 'Resolved'}
                      </Badge>
                    </div>

                    <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2 text-xs text-muted-foreground">
                      {issue.location_detail && (
                        <span className="inline-flex items-center gap-1.5">
                          <MapPin className="size-3.5" />
                          {issue.location_detail.name}
                        </span>
                      )}
                      {issue.trade_detail && (
                        <span className="inline-flex items-center gap-1.5">
                          <Wrench className="size-3.5" />
                          {issue.trade_detail.name}
                        </span>
                      )}
                      <span className="inline-flex items-center gap-1.5">
                        <UserRound className="size-3.5" />
                        {creatorName(issue)}
                      </span>
                      <span>{format(parseISO(issue.created_at), 'MMM d, yyyy · h:mm a')}</span>
                    </div>

                    {canModify && (
                      <div className="mt-4 flex flex-wrap gap-2 border-t border-border pt-3">
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={updateStatus.isPending}
                          onClick={() =>
                            updateStatus.mutate({
                              issueId: issue.id,
                              status: isOpen ? 'RESOLVED' : 'OPEN',
                            })
                          }
                        >
                          {isOpen ? <CheckCircle2 className="mr-1" /> : <RotateCcw />}
                          {isOpen ? 'Mark resolved' : 'Reopen'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setActiveIssue(issue);
                            setMode('edit');
                          }}
                        >
                          <Pencil className="mr-1" />
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                          onClick={() => {
                            setActiveIssue(issue);
                            setMode('delete');
                          }}
                        >
                          <Trash2 className="mr-1" />
                          Delete
                        </Button>
                      </div>
                    )}
                  </article>
                );
              })}
            </div>

            {error && (
              <p role="alert" className="text-sm text-destructive">
                {getErrorMessage(error)}
              </p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
